import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Building2, Landmark, MapPin, Globe, Mail, Phone, ShieldCheck, Award,
  FileText, Users, Activity, TrendingUp, KeyRound, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { TIER_STYLE, CATEGORY_LABEL, TierBadge, ReputationBadge } from "@/components/network/TierBadge";

export default function OrganisationProfile() {
  const { id } = useParams();
  const [org, setOrg] = useState(null);
  const [updates, setUpdates] = useState([]);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/network/organisations/${id}`);
      setOrg(r.data);
      const u = await api.get(`/network/organisations/${id}/updates`);
      setUpdates(u.data);
    })();
  }, [id]);

  if (!org) return (
    <div><Navbar /><div className="max-w-[1400px] mx-auto px-6 py-10 mono text-sm">Loading organisation…</div></div>
  );
  const s = TIER_STYLE[org.tier] || TIER_STYLE.bronze;
  const rep = org.reputation || {};

  return (
    <div>
      <Navbar />

      {/* Corporate certification header */}
      <section className="border-b border-border bg-card">
        <div className="max-w-[1400px] mx-auto px-6 py-10 grid md:grid-cols-[auto_1fr_auto] gap-8 items-start">
          <div
            className="h-28 w-28 rounded-sm grid place-items-center border-2"
            style={{ background: `${s.ring}18`, borderColor: `${s.ring}88` }}
            data-testid="org-avatar"
          >
            {org.category === "government"
              ? <Landmark className="h-12 w-12" style={{ color: s.ring }} />
              : <Building2 className="h-12 w-12" style={{ color: s.ring }} />}
          </div>
          <div>
            <div className="flex items-center gap-2 text-[10px] mono uppercase tracking-widest text-muted-foreground">
              THOM Verify Trust Network™ · Accredited Partner
              {org.status === "verified" && <ShieldCheck className="h-3 w-3 text-primary" />}
            </div>
            <h1 className="display text-4xl mt-1" data-testid="org-name">{org.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span>{CATEGORY_LABEL[org.category] || org.category}</span>
              <span className="h-1 w-1 rounded-full bg-muted-foreground/50" />
              <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {org.city}, {org.country}</span>
              {org.registration_number && <><span className="h-1 w-1 rounded-full bg-muted-foreground/50" /><span className="mono">Reg. {org.registration_number}</span></>}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <TierBadge tier={org.tier} size="lg" />
              <ReputationBadge rep={rep} size="lg" />
              {org.status === "verified" ? (
                <span className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-300 inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-3 w-3" /> Verified · {org.verified_at ? new Date(org.verified_at).toLocaleDateString() : ""}
                </span>
              ) : (
                <span className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-300 inline-flex items-center gap-1.5">
                  <AlertTriangle className="h-3 w-3" /> Pending Review
                </span>
              )}
            </div>
          </div>
          <div className="border border-border p-4 min-w-[240px] bg-background">
            <div className="kicker">Digital Certificate</div>
            <div className="mono text-xs mt-2 break-all">TVN-{org.id.slice(-10).toUpperCase()}</div>
            <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
              Issued by THOM Verify™ Trust Authority under partner accreditation programme.
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-[1400px] mx-auto px-6 py-8 grid lg:grid-cols-[1fr_320px] gap-8">
        <div>
          <Tabs defaultValue="overview">
            <TabsList>
              <TabsTrigger value="overview" data-testid="org-tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="services" data-testid="org-tab-services">Verified Services</TabsTrigger>
              <TabsTrigger value="updates" data-testid="org-tab-updates">Trusted Updates ({updates.length})</TabsTrigger>
              <TabsTrigger value="team" data-testid="org-tab-team">Executive Contacts</TabsTrigger>
              <TabsTrigger value="compliance" data-testid="org-tab-compliance">Compliance</TabsTrigger>
              <TabsTrigger value="audit" data-testid="org-tab-audit">Audit History</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-6 space-y-4">
              {org.description && (
                <div className="border border-border p-6 bg-card">
                  <div className="kicker">About</div>
                  <p className="mt-2 leading-relaxed">{org.description}</p>
                </div>
              )}
              <div className="grid sm:grid-cols-3 gap-3">
                <MiniStat label="Trusted updates" value={rep.signals?.accepted_updates || 0} Icon={Activity} />
                <MiniStat label="Assets touched" value={rep.signals?.assets_touched || 0} Icon={ShieldCheck} />
                <MiniStat label="Tenure" value={`${rep.signals?.tenure_days || 0}d`} Icon={TrendingUp} />
              </div>
              <div className="border border-border p-6 bg-card">
                <div className="kicker">Operating Regions</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="text-xs px-2 py-1 border border-border rounded-sm">{org.city}</span>
                  <span className="text-xs px-2 py-1 border border-border rounded-sm">{org.country}</span>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="services" className="mt-6">
              <div className="border border-border p-6 bg-card">
                <div className="kicker">Accredited to record</div>
                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                  {(org.tier ? TIER_KINDS[org.tier] || [] : []).map(k => (
                    <div key={k} className="flex items-center gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      <span>{k.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}</span>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="updates" className="mt-6">
              {updates.length === 0 ? (
                <div className="border border-border p-6 bg-card mono text-sm text-muted-foreground">No trusted updates yet.</div>
              ) : (
                <div className="border border-border bg-card divide-y divide-border">
                  {updates.map(u => (
                    <div key={u.id} className="p-4 flex items-start gap-4" data-testid={`org-update-${u.id}`}>
                      <div className="h-8 w-8 grid place-items-center border border-border rounded-sm">
                        <FileText className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="mono uppercase tracking-widest">{u.kind.replace(/_/g, " ")}</span>
                          <span>·</span>
                          <span>{new Date(u.created_at).toLocaleDateString()}</span>
                        </div>
                        <div className="display text-base leading-tight mt-0.5">{u.title}</div>
                        {u.detail && <div className="text-sm text-muted-foreground mt-1">{u.detail}</div>}
                        <div className="mt-2 flex flex-wrap gap-2 text-[10px] mono uppercase tracking-widest text-muted-foreground">
                          <span>Asset {u.asset_id.slice(-8)}</span>
                          {u.mileage_km && <span>{u.mileage_km.toLocaleString()} km</span>}
                          {u.cost_usd && <span>USD {u.cost_usd.toLocaleString()}</span>}
                          {u.reference_number && <span>Ref {u.reference_number}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="team" className="mt-6">
              <div className="border border-border bg-card">
                <div className="p-4 border-b border-border kicker">Members ({(org.members || []).length})</div>
                {(org.members || []).length === 0 ? (
                  <div className="p-6 mono text-sm text-muted-foreground">No listed contacts yet.</div>
                ) : org.members.map(m => (
                  <div key={m.id} className="p-4 flex items-center gap-3 border-b border-border last:border-0">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{m.user?.name || m.user?.email}</div>
                      <div className="text-xs text-muted-foreground">{m.user?.email}</div>
                    </div>
                    <span className="mono text-[10px] uppercase tracking-widest px-2 py-0.5 border border-border">{m.role}</span>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="compliance" className="mt-6">
              <div className="border border-border p-6 bg-card space-y-3">
                <CompRow label="Trust Network Accreditation" state="ok" />
                <CompRow label="Digital Certificate" state="ok" />
                <CompRow label="API Access Enabled" state={["gold","platinum","government"].includes(org.tier) ? "ok" : "na"} />
                <CompRow label="Document Verification Privileges" state={["silver","gold","platinum","government"].includes(org.tier) ? "ok" : "na"} />
                <CompRow label="Bulk Issuance Privileges" state={["gold","platinum","government"].includes(org.tier) ? "ok" : "na"} />
              </div>
            </TabsContent>

            <TabsContent value="audit" className="mt-6">
              <div className="border border-border p-6 bg-card">
                <div className="kicker">Immutable Ledger</div>
                <div className="text-sm mt-2 mono text-muted-foreground">
                  Every action performed by this organisation is written to the THOM Verify™ audit ledger and mirrored into the Digital Passport of the affected asset.
                </div>
                <div className="mt-3 text-xs mono text-muted-foreground">Organisation ID: {org.id}</div>
                <div className="text-xs mono text-muted-foreground">Registered: {new Date(org.created_at).toLocaleString()}</div>
                {org.verified_at && <div className="text-xs mono text-muted-foreground">Accredited: {new Date(org.verified_at).toLocaleString()}</div>}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right rail — contact card */}
        <aside className="space-y-4">
          <div className="border border-border p-5 bg-card">
            <div className="kicker">Contact</div>
            <div className="mt-3 space-y-2 text-sm">
              {org.website && <a href={org.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-primary"><Globe className="h-3.5 w-3.5" /> {org.website}</a>}
              {org.contact_email && <a href={`mailto:${org.contact_email}`} className="flex items-center gap-2 hover:text-primary"><Mail className="h-3.5 w-3.5" /> {org.contact_email}</a>}
              {org.contact_phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {org.contact_phone}</div>}
              {org.address && <div className="flex items-center gap-2"><MapPin className="h-3.5 w-3.5" /> {org.address}</div>}
            </div>
          </div>
          <div className="border border-border p-5 bg-card">
            <div className="kicker">Reputation Signals</div>
            <div className="mt-3 space-y-2 text-sm">
              <SignalRow label="Score" value={rep.score || 0} />
              <SignalRow label="Band" value={rep.band || "New"} />
              <SignalRow label="Accepted updates" value={rep.signals?.accepted_updates || 0} />
              <SignalRow label="Disputes" value={rep.signals?.disputed_updates || 0} />
              <SignalRow label="Assets touched" value={rep.signals?.assets_touched || 0} />
            </div>
          </div>
          <Link to="/network"><Button variant="outline" className="w-full" data-testid="btn-back-directory">← Back to directory</Button></Link>
        </aside>
      </section>
      <Footer />
    </div>
  );
}

const MiniStat = ({ label, value, Icon }) => (
  <div className="border border-border p-4 bg-card">
    <div className="flex items-center justify-between">
      <div className="kicker">{label}</div>
      <Icon className="h-4 w-4 text-primary" />
    </div>
    <div className="display text-2xl mt-1">{value}</div>
  </div>
);

const SignalRow = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="mono text-xs">{value}</div>
  </div>
);

const CompRow = ({ label, state }) => (
  <div className="flex items-center justify-between text-sm">
    <div>{label}</div>
    {state === "ok"
      ? <span className="mono text-[10px] uppercase tracking-widest text-emerald-700 border border-emerald-500/40 bg-emerald-50 px-2 py-0.5 rounded-sm">Compliant</span>
      : <span className="mono text-[10px] uppercase tracking-widest text-muted-foreground border px-2 py-0.5 rounded-sm">Not on tier</span>}
  </div>
);

const TIER_KINDS = {
  bronze:     ["service_recorded"],
  silver:     ["service_recorded", "insurance_recorded"],
  gold:       ["service_recorded", "insurance_recorded", "accident_reported"],
  platinum:   ["service_recorded", "insurance_recorded", "accident_reported", "ownership_transferred"],
  government: ["service_recorded", "insurance_recorded", "accident_reported", "ownership_transferred", "duty_confirmed", "scrapped"],
};
